"use client";

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { SkillMarkdownEditor } from "@/components/command-skills/skill-markdown-editor";
import {
  sanitizeDescription,
  SKILL_DESCRIPTION_MAX,
  validateSkillDescription,
  validateSkillInstructions,
} from "@/components/command-skills/skill-utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { SkillDetail } from "@/types/command-skill";

interface EditSkillDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  skill: SkillDetail;
  pending: boolean;
  onSubmit: (body: { description?: string; instructions?: string }) => void;
}

export function EditSkillDialog({
  open,
  onOpenChange,
  skill,
  pending,
  onSubmit,
}: EditSkillDialogProps) {
  const [description, setDescription] = useState(skill.description);
  const [instructions, setInstructions] = useState(skill.instructions);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [instructionsError, setInstructionsError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDescription(skill.description);
      setInstructions(skill.instructions);
      setDescriptionError(null);
      setInstructionsError(null);
    }
  }, [open, skill.description, skill.instructions]);

  const submit = () => {
    const nextDescriptionError = validateSkillDescription(description);
    const nextInstructionsError = validateSkillInstructions(instructions);
    setDescriptionError(nextDescriptionError);
    setInstructionsError(nextInstructionsError);
    if (nextDescriptionError || nextInstructionsError) return;
    const body: { description?: string; instructions?: string } = {};
    if (description !== skill.description) body.description = description.trim();
    if (instructions !== skill.instructions) body.instructions = instructions;
    onSubmit(body);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !pending && onOpenChange(next)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Edit details</DialogTitle>
          <DialogDescription>
            Update the description and instructions for{" "}
            <span className="font-mono">{skill.name}</span>. The name stays the same.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="edit-skill-description">Description</Label>
              <span className="text-[11px] text-muted-foreground">
                {description.length}/{SKILL_DESCRIPTION_MAX}
              </span>
            </div>
            <Textarea
              id="edit-skill-description"
              value={description}
              rows={3}
              onChange={(event) => {
                const next = sanitizeDescription(event.target.value);
                setDescription(next);
                setDescriptionError(next ? validateSkillDescription(next) : null);
              }}
            />
            <p className="text-xs text-muted-foreground">
              Say what it does <span className="font-medium text-foreground">and when to use it</span>.
              The agent decides from this text whether to use the skill.
            </p>
            {descriptionError ? <p className="text-sm text-destructive">{descriptionError}</p> : null}
          </div>
          <div className="space-y-2">
            <Label>Instructions</Label>
            <SkillMarkdownEditor
              value={instructions}
              onChange={(next) => {
                setInstructions(next);
                setInstructionsError(next ? validateSkillInstructions(next) : null);
              }}
            />
            {instructionsError ? <p className="text-sm text-destructive">{instructionsError}</p> : null}
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={pending} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={pending} onClick={submit}>
            {pending ? <Loader2 className="size-4 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
